import { gql } from '@apollo/client';
import { createContext, useContext } from 'react';
import { initializeApollo } from '../services/apolloClient';
import { ICOParticipationWatchlistSubscriptionStatus } from '../models/ico';

const apolloClient = initializeApollo();

interface ICOContextData {
    subscribeToWatchlist: () => Promise<boolean>;
    unsubscribFromWatchlist: () => Promise<boolean>;
    isInWatchlist: () => Promise<ICOParticipationWatchlistSubscriptionStatus>;
}

const ICOContext = createContext<ICOContextData>({} as ICOContextData);

export const ICOProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
    const subscribeToWatchlist = async (): Promise<boolean> => {
        return new Promise<boolean>((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: gql`
                        mutation {
                            ICOParticipationWatchlistSubscribe
                        }
                    `,
                })
                .then((response) => {
                    const graphqlResponse = response.data?.ICOParticipationWatchlistSubscribe;
                    resolve(graphqlResponse);
                })
                .catch((error) => {
                    reject(error.response?.data ? error.response.data : error.message);
                });
        });
    };

    const unsubscribFromWatchlist = async (): Promise<boolean> => {
        return new Promise<boolean>((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: gql`
                        mutation {
                            ICOParticipationWatchlistUnsubscribe
                        }
                    `,
                })
                .then((response) => {
                    const graphqlResponse = response.data?.ICOParticipationWatchlistUnsubscribe;
                    resolve(graphqlResponse);
                })
                .catch((error) => {
                    reject(error.response?.data ? error.response.data : error.message);
                });
        });
    };

    const isInWatchlist = async (): Promise<ICOParticipationWatchlistSubscriptionStatus> => {
        return new Promise<ICOParticipationWatchlistSubscriptionStatus>((resolve, reject) => {
            apolloClient
                .query({
                    query: gql`
                        query {
                            ICOParticipationWatchlistSubscriptionStatus {
                                status
                            }
                        }
                    `,
                })
                .then(async response => {
                    resolve(response.data?.ICOParticipationWatchlistSubscriptionStatus);
                })
                .catch((error) => {
                    reject(error.response?.data ? error.response.data : error.message);
                });
        });
    };

  return (
    <ICOContext.Provider value={{ subscribeToWatchlist, unsubscribFromWatchlist, isInWatchlist }}>
      {children}
    </ICOContext.Provider>
  );
};

function useICO(): ICOContextData {
  const context = useContext(ICOContext);

  if (!context) {
    throw new Error('useICO must be used within an ICOProvider');
  }
  return context;
}

export { ICOContext, useICO };
